import React, { useState, useEffect } from 'react';
import {
  RiCloseLine,
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiSaveLine,
  RiCloseFill,
  RiFileTextLine,
  RiToolsLine,
  RiMoneyDollarCircleLine,
  RiTruckLine,
  RiArrowGoBackLine,
  RiMoreLine,
  RiUser3Line,
  RiCalendarLine,
  RiStickyNoteLine
} from 'react-icons/ri';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePermission } from '../../hooks/usePermission';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { rawApi as api } from '../../utils/apiWithLogging';
import { showAlert, showConfirm } from '../../utils/alert';

export interface RentalNote {
  id: number;
  rental_id: number;
  note: string;
  note_type: 'GENERAL' | 'MAINTENANCE' | 'PAYMENT' | 'DELIVERY' | 'RETURN' | 'OTHER';
  created_by_user_id: number;
  created_by_username?: string;
  created_by_fullname?: string;
  created_at: string;
  updated_at: string;
}

interface RentalNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number;
  rentalCode: string;
}

const RentalNotesModal: React.FC<RentalNotesModalProps> = ({
  isOpen,
  onClose,
  rentalId,
  rentalCode
}) => {
  const { user } = useCurrentUser();
  // BUG-S2167-001: POST/PUT/DELETE /rentals/notes ต้องมี rentals.edit (rental_notes.py) — เดิมโมดัลนี้
  // (เปิดจากหน้ารายการ "ดูหมายเหตุ" / หน้าแก้ไข) ไม่ตรวจเลย Viewer/Accounting เห็นปุ่ม "เพิ่มหมายเหตุ"
  // กรอกเสร็จแล้วโดน 403 · ใช้เกณฑ์เดียวกับ RentalNotesSection ในหน้ารายละเอียด
  const { hasPermission } = usePermission();
  const canWriteNotes = hasPermission('rentals.edit');
  const [notes, setNotes] = useState<RentalNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<RentalNote | null>(null);
  const [newNote, setNewNote] = useState({
    note: '',
    note_type: 'GENERAL' as RentalNote['note_type']
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});

  // โหลดข้อมูลหมายเหตุ
  const loadNotes = async () => {
    if (!rentalId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/rentals/${rentalId}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error loading rental notes:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดหมายเหตุได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  // เพิ่มหมายเหตุใหม่
  const handleAddNote = async () => {
    const errors: Record<string, string> = {};

    if (!newNote.note.trim()) {
      errors.note = 'กรุณาใส่เนื้อหาหมายเหตุ';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Focus ไปที่ textarea
      const noteTextarea = document.querySelector('textarea[name="new_note"]');
      if (noteTextarea) {
        (noteTextarea as HTMLTextAreaElement).focus();
      }
      return;
    }

    setLoading(true);
    try {
      await api.post(`/rentals/${rentalId}/notes`, newNote);
      showAlert('สำเร็จ', 'เพิ่มหมายเหตุเรียบร้อยแล้ว', 'success');
      setNewNote({ note: '', note_type: 'GENERAL' });
      setFormErrors({});
      setShowAddForm(false);
      await loadNotes();
    } catch (error) {
      console.error('Error adding note:', error);
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถเพิ่มหมายเหตุได้'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // แก้ไขหมายเหตุ
  const handleUpdateNote = async (noteId: number, updatedData: Partial<RentalNote>) => {
    const errors: Record<string, string> = {};

    if (!updatedData.note?.trim()) {
      errors.note = 'กรุณาใส่เนื้อหาหมายเหตุ';
    }

    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      // Focus ไปที่ textarea ของโหมดแก้ไข
      const editTextarea = document.querySelector(`textarea[name="edit_note_${noteId}"]`);
      if (editTextarea) {
        (editTextarea as HTMLTextAreaElement).focus();
      }
      return;
    }

    setLoading(true);
    try {
      await api.put(`/rentals/notes/${noteId}`, updatedData);
      showAlert('สำเร็จ', 'แก้ไขหมายเหตุเรียบร้อยแล้ว', 'success');
      setEditingNote(null);
      setEditFormErrors({});
      await loadNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถแก้ไขหมายเหตุได้'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ลบหมายเหตุ
  const handleDeleteNote = async (noteId: number) => {
    const confirmed = await showConfirm(
      'ยืนยันการลบ',
      'คุณต้องการลบหมายเหตุนี้หรือไม่?',
      undefined,
      { type: 'warning' }
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      await api.delete(`/rentals/notes/${noteId}`);
      showAlert('สำเร็จ', 'ลบหมายเหตุเรียบร้อยแล้ว', 'success');
      await loadNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถลบหมายเหตุได้'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // โหลดข้อมูลเมื่อเปิด modal
  useEffect(() => {
    if (isOpen && rentalId) {
      loadNotes();
      // รีเซ็ตฟอร์ม
      setShowAddForm(false);
      setEditingNote(null);
      setNewNote({ note: '', note_type: 'GENERAL' });
      setFormErrors({});
      setEditFormErrors({});
    }
  }, [isOpen, rentalId]);

  // แสดงไอคอนตามประเภทหมายเหตุ
  const getNoteTypeIcon = (type: RentalNote['note_type']) => {
    switch (type) {
      case 'GENERAL':
        return <RiFileTextLine className="w-4 h-4" />;
      case 'MAINTENANCE':
        return <RiToolsLine className="w-4 h-4" />;
      case 'PAYMENT':
        return <RiMoneyDollarCircleLine className="w-4 h-4" />;
      case 'DELIVERY':
        return <RiTruckLine className="w-4 h-4" />;
      case 'RETURN':
        return <RiArrowGoBackLine className="w-4 h-4" />;
      case 'OTHER':
        return <RiMoreLine className="w-4 h-4" />;
      default:
        return <RiFileTextLine className="w-4 h-4" />;
    }
  };

  // แสดงข้อความประเภทหมายเหตุ
  const getNoteTypeLabel = (type: RentalNote['note_type']) => {
    switch (type) {
      case 'GENERAL':
        return 'ทั่วไป';
      case 'MAINTENANCE':
        return 'การบำรุงรักษา';
      case 'PAYMENT':
        return 'การชำระเงิน';
      case 'DELIVERY':
        return 'การส่งมอบ';
      case 'RETURN':
        return 'การคืนอุปกรณ์';
      case 'OTHER':
        return 'อื่นๆ';
      default:
        return type;
    }
  };

  // แสดงสีตามประเภทหมายเหตุ
  const getNoteTypeColor = (type: RentalNote['note_type']) => {
    switch (type) {
      case 'GENERAL':
        return 'bg-primary-100 text-primary-800';
      case 'MAINTENANCE':
        return 'bg-warning-100 text-warning-800';
      case 'PAYMENT':
        return 'bg-success-100 text-success-800';
      case 'DELIVERY':
        return 'bg-accent-100 text-accent-800';
      case 'RETURN':
        return 'bg-danger-100 text-danger-800';
      case 'OTHER':
        return 'bg-secondary-100 text-secondary-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  // ฟอร์แมตวันที่
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-primary-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <RiFileTextLine className="w-6 h-6 mr-2" />
            <div>
              <h2 className="text-lg font-semibold">หมายเหตุการเช่า</h2>
              <p className="text-sm text-primary-100">รหัสการเช่า: {rentalCode}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="ปิด"
            className="text-white hover:text-primary-200 transition-colors"
          >
            <RiCloseLine className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Add Note Button */}
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-medium text-secondary-900">
              รายการหมายเหตุ ({notes.length})
            </h3>
            {canWriteNotes && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <RiAddLine className="w-4 h-4 mr-2" />
              เพิ่มหมายเหตุ
            </button>
            )}
          </div>

          {/* Add Note Form */}
          {canWriteNotes && showAddForm && (
            <div className="mb-6 p-4 border border-secondary-200 rounded-lg bg-secondary-50">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-3">
                <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiAddLine className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-secondary-800">เพิ่มหมายเหตุใหม่</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    ประเภท
                  </label>
                  <select
                    aria-label="ประเภท"
                    value={newNote.note_type}
                    onChange={(e) => setNewNote({ ...newNote, note_type: e.target.value as RentalNote['note_type'] })}
                    className="select"
                  >
                    <option value="GENERAL">ทั่วไป</option>
                    <option value="MAINTENANCE">การบำรุงรักษา</option>
                    <option value="PAYMENT">การชำระเงิน</option>
                    <option value="DELIVERY">การส่งมอบ</option>
                    <option value="RETURN">การคืนอุปกรณ์</option>
                    <option value="OTHER">อื่นๆ</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    เนื้อหาหมายเหตุ <span className="text-danger-600">*</span>
                  </label>
                  <div className="flex">
                    <textarea
                      name="new_note"
                      value={newNote.note}
                      onChange={(e) => {
                        setNewNote({ ...newNote, note: e.target.value });
                        if (formErrors.note) {
                          setFormErrors({ ...formErrors, note: '' });
                        }
                      }}
                      placeholder="ใส่เนื้อหาหมายเหตุ..."
                      rows={2}
                      className={`input flex-1${formErrors.note ? ' border-danger-500' : ''}`}
                      aria-invalid={Boolean(formErrors.note)}
                      aria-describedby={formErrors.note ? 'new_note-error' : undefined}
                    />
                    <div className="flex flex-col">
                      <button
                        onClick={handleAddNote}
                        aria-label="บันทึกหมายเหตุ"
                        title="บันทึกหมายเหตุ"
                        disabled={loading}
                        className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-tr-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RiSaveLine className="w-4 h-4" />
                      </button>
                      <button
                        aria-label="ยกเลิกการเพิ่มหมายเหตุ"
                        title="ยกเลิกการเพิ่มหมายเหตุ"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewNote({ note: '', note_type: 'GENERAL' });
                          setFormErrors({});
                        }}
                        className="flex-1 bg-secondary-500 text-white px-4 py-2 rounded-br-md hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      >
                        <RiCloseFill className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {formErrors.note && (
                    <p id="new_note-error" className="mt-1 text-sm text-danger-600">{formErrors.note}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-secondary-500">กำลังโหลด...</p>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8">
              <RiFileTextLine className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-500">ยังไม่มีหมายเหตุ</p>
              <p className="text-sm text-secondary-500 mt-1">กดปุ่ม "เพิ่มหมายเหตุ" เพื่อเริ่มบันทึกหมายเหตุ</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => (
                <div key={note.id} className="border border-secondary-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  {editingNote?.id === note.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            ประเภท
                          </label>
                          <select
                            aria-label="ประเภท"
                            value={editingNote.note_type}
                            onChange={(e) => setEditingNote({ ...editingNote, note_type: e.target.value as RentalNote['note_type'] })}
                            className="select"
                          >
                            <option value="GENERAL">ทั่วไป</option>
                            <option value="MAINTENANCE">การบำรุงรักษา</option>
                            <option value="PAYMENT">การชำระเงิน</option>
                            <option value="DELIVERY">การส่งมอบ</option>
                            <option value="RETURN">การคืนอุปกรณ์</option>
                            <option value="OTHER">อื่นๆ</option>
                          </select>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            เนื้อหาหมายเหตุ <span className="text-danger-600">*</span>
                          </label>
                          <textarea
                            aria-label="เนื้อหาหมายเหตุ"
                            name={`edit_note_${note.id}`}
                            value={editingNote.note}
                            onChange={(e) => {
                              setEditingNote({ ...editingNote, note: e.target.value });
                              if (editFormErrors.note) {
                                setEditFormErrors({ ...editFormErrors, note: '' });
                              }
                            }}
                            rows={3}
                            className={`input${editFormErrors.note ? ' border-danger-500' : ''}`}
                            aria-invalid={Boolean(editFormErrors.note)}
                            aria-describedby={editFormErrors.note ? `edit_note_${note.id}-error` : undefined}
                          />
                          {editFormErrors.note && (
                            <p id={`edit_note_${note.id}-error`} className="mt-1 text-sm text-danger-600">{editFormErrors.note}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleUpdateNote(note.id, {
                            note: editingNote.note,
                            note_type: editingNote.note_type
                          })}
                          disabled={loading}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                          <RiSaveLine className="w-4 h-4 mr-1" />
                          บันทึก
                        </button>
                        <button
                          onClick={() => {
                            setEditingNote(null);
                            setEditFormErrors({});
                          }}
                          className="inline-flex items-center px-3 py-1 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <RiCloseFill className="w-4 h-4 mr-1" />
                          ยกเลิก
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getNoteTypeColor(note.note_type)}`}>
                            {getNoteTypeIcon(note.note_type)}
                            <span className="ml-1">{getNoteTypeLabel(note.note_type)}</span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {canWriteNotes && (user?.id === note.created_by_user_id || user?.is_superuser) && (
                            <>
                              <button
                                onClick={() => setEditingNote(note)}
                                className="text-secondary-400 hover:text-primary-600 transition-colors"
                                title="แก้ไข"
                              >
                                <RiEditLine className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="text-secondary-400 hover:text-danger-600 transition-colors"
                                title="ลบ"
                              >
                                <RiDeleteBinLine className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-secondary-900 mb-3 whitespace-pre-wrap [overflow-wrap:anywhere]">{note.note}</p>
                      <div className="flex items-center justify-between text-sm text-secondary-500">
                        <div className="flex items-center">
                          <RiUser3Line className="w-4 h-4 mr-1" />
                          {/* G2 (จุดพี่น้อง): ปกติ backend ส่งชื่อมาให้แล้ว (rental_notes.py:63-64)
                              เหลือกรณีผู้ใช้ถูกลบ → created_by_user เป็น NULL ทั้งคู่
                              ใช้ถ้อยคำไทยชุดเดียวกับหน้าเอกสาร แทน "User 47" ที่ผู้ใช้อ่านไม่รู้เรื่อง */}
                          <span>
                            {note.created_by_fullname?.trim() ||
                              note.created_by_username?.trim() ||
                              (note.created_by_user_id ? `ผู้ใช้ #${note.created_by_user_id}` : 'ไม่ระบุ')}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <RiCalendarLine className="w-4 h-4 mr-1" />
                          <span>{formatDate(note.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-secondary-50 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default RentalNotesModal;